import React, { useEffect } from 'react';
import { BodyText, Button, Message, Stack, Title } from '@kvdbil/components';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import TextFieldWithControl from '~/App/shared/components/Fields/TextFieldWithControl';
import {
  member as memberSelector,
  isEmailVerified as isEmailVerifiedSelector
} from '~/App/shared/selectors/sessionSelector';
import { getCountryFromValue } from '~/helpers/countries';
import { useTranslation } from '~/Locale';
import {
  fieldPropsAddress,
  fieldPropsCity,
  fieldPropsPostalCode
} from '~/App/shared/components/Fields/shared/fieldProps';
import {
  CountryField,
  OrganisationNumberField,
  PhoneNumberField,
  VatNumberField
} from '~/App/shared/components/Fields/shared';
import { regexPhoneNumber } from '~/helpers/regex';
import CheckBoxWithControl from '~/App/shared/components/Fields/CheckBoxWithControl';
import BankIdAuthButton from '~/App/shared/components/Auth/components/BankIdAuthButton';
import {
  phoneVerificationIsUpdatingPhoneNumber,
  verifyPhoneNumberSelector
} from '~/App/shared/selectors/verifyPhoneNumberSelector';
import { usePrevious } from '~/App/shared/hooks/usePrevious';
import { updateMemberPhoneNumber } from '~/App/shared/actions/verifyPhoneNumber';
import { AccountVerificationStatus } from '../../hooks/useAccountVerificationStatus';
import EmailVerificationRequiredMessage from '~/App/shared/components/VerifyPhoneNumber/components/EmailVerificationRequiredMessage';

export type FormData = {
  address: string;
  zipCode: string;
  city: string;
  country: {
    label: string;
    value: string;
  };
  automaticAddressEnabled: boolean;
  organisationNumber: string;
  vatNumber: string;
  phoneNumber: string;
};

interface Props {
  organisationDetailsFormData: FormData | null;
  setOrganisationDetailsFormData(formData: FormData | null): void;
  handleSubmitButtonClick(formData: FormData): void;
  accountVerificationStatus: Extract<
    AccountVerificationStatus,
    | 'VERIFY_ORG_PERSON_BANKID_AND_PHONE'
    | 'VERIFY_ORG_PERSON_BANKID'
    | 'VERIFY_ORG_PERSON_PHONE'
    | 'VERIFY_ORG_MEMBER_DATA'
  >;
}

const OrganisationMemberDetailsForm = ({
  organisationDetailsFormData,
  setOrganisationDetailsFormData,
  handleSubmitButtonClick,
  accountVerificationStatus
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUpdatingPhoneNumber = useSelector(
    phoneVerificationIsUpdatingPhoneNumber
  );
  const { error, generalError } = useSelector(verifyPhoneNumberSelector);
  const previousIsUpdatingPhoneNumber = usePrevious<undefined | boolean>(
    isUpdatingPhoneNumber
  );
  const member = useSelector(memberSelector);
  const { addresses, automaticAddressEnabled } = member;
  const { phoneNumberVerified, phoneNumber, organisation } =
    member?.person ?? {};
  const { organisationNumber, vatNumber } = organisation ?? {};
  const address = addresses?.[0];
  const isError = error || generalError;
  const isEmailVerified = useSelector(isEmailVerifiedSelector);

  const {
    handleSubmit,
    control,
    watch,
    trigger,
    getValues,
    formState: { dirtyFields, isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: organisationDetailsFormData ?? {
      address: address?.address1 ?? '',
      zipCode: address?.zipCode ?? '',
      city: address?.city ?? '',
      country: getCountryFromValue(address?.country ?? '', t) ?? {
        value: '',
        label: ''
      },
      automaticAddressEnabled: automaticAddressEnabled ?? false,
      organisationNumber: organisationNumber ?? '',
      vatNumber: vatNumber ?? '',
      phoneNumber: phoneNumber ?? ''
    }
  });

  useEffect(() => {
    const prefilledFields = (['address', 'city'] as const).filter(field =>
      getValues(field)
    );
    if (prefilledFields.length > 0) {
      void trigger(prefilledFields);
    }
  }, [getValues, trigger]);

  useEffect(() => {
    if (previousIsUpdatingPhoneNumber && !isUpdatingPhoneNumber && !isError) {
      organisationDetailsFormData &&
        handleSubmitButtonClick(organisationDetailsFormData);
    }
  }, [
    previousIsUpdatingPhoneNumber,
    isUpdatingPhoneNumber,
    isError,
    handleSubmitButtonClick,
    organisationDetailsFormData
  ]);

  const countryValue = watch('country')?.value;
  const isPhoneNumberDirty = Boolean(dirtyFields?.phoneNumber);
  const shouldDisplayEmailVerificationError =
    !phoneNumberVerified && !isEmailVerified;

  const onSubmit = (formData: FormData) => {
    setOrganisationDetailsFormData(formData);
    isPhoneNumberDirty &&
      dispatch(updateMemberPhoneNumber(formData.phoneNumber, addresses));
    !isPhoneNumberDirty && handleSubmitButtonClick(formData);
  };

  return (
    <Stack
      spacing={2}
      data-testid={accountVerificationStatus.replaceAll('_', '-').toLowerCase()}
    >
      <Stack>
        <Title as="h3" style={{ textAlign: 'center' }}>
          {t('Verify your information')}
        </Title>
        <BodyText>{t('You must verify your information to proceed.')}</BodyText>
      </Stack>
      <Stack style={{ textAlign: 'left' }}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <Stack>
              <TextFieldWithControl
                control={control}
                {...fieldPropsAddress({ t })}
              />
              <TextFieldWithControl
                control={control}
                {...fieldPropsPostalCode({ t, country: countryValue })}
              />
              <TextFieldWithControl
                control={control}
                {...fieldPropsCity({ t })}
              />
              <CountryField
                control={control}
                t={t}
                isDisabled={Boolean(address?.country)}
              />
              <CheckBoxWithControl
                control={control}
                name="automaticAddressEnabled"
                label={t(
                  'Allow fetching my address automatically in the future.'
                )}
                color="info"
              />
              <OrganisationNumberField
                control={control}
                isDisabled={Boolean(organisationNumber)}
                t={t}
                country={countryValue}
              />
              {countryValue !== 'se' && (
                <VatNumberField control={control} t={t} />
              )}
              {!phoneNumberVerified && (
                <PhoneNumberField
                  control={control}
                  name="phoneNumber"
                  label={t('Mobile number')}
                  t={t}
                  rules={{
                    pattern: {
                      value: regexPhoneNumber,
                      message: t('Invalid phone number')
                    }
                  }}
                  isDisabled={!isEmailVerified}
                />
              )}
            </Stack>

            {isError && (
              <Message type="error" fullWidth>
                {t('Something went wrong, please try again later.')}
              </Message>
            )}

            {shouldDisplayEmailVerificationError && (
              <EmailVerificationRequiredMessage />
            )}

            {(accountVerificationStatus ===
              'VERIFY_ORG_PERSON_BANKID_AND_PHONE' ||
              accountVerificationStatus === 'VERIFY_ORG_PERSON_BANKID') && (
              <BankIdAuthButton
                fullWidth
                isLoading={isUpdatingPhoneNumber}
                isDisabled={!isValid || shouldDisplayEmailVerificationError}
                type="submit"
              >
                {t('Verify with BankID')}
              </BankIdAuthButton>
            )}

            {(accountVerificationStatus === 'VERIFY_ORG_PERSON_PHONE' ||
              accountVerificationStatus === 'VERIFY_ORG_MEMBER_DATA') && (
              <Button
                color="secondary"
                size="regular"
                fullWidth
                isLoading={isUpdatingPhoneNumber}
                isDisabled={!isValid || shouldDisplayEmailVerificationError}
                type="submit"
              >
                {accountVerificationStatus === 'VERIFY_ORG_MEMBER_DATA'
                  ? t('Save information')
                  : t('Send verification code')}
              </Button>
            )}
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};

export default OrganisationMemberDetailsForm;
